import React from 'react';
import { CalendarDayProps } from '../types/calendar';
import { formatTime } from '../utils/calendarUtils';
import WorkoutSlot from './WorkoutSlot';
import MultipleWorkoutsSlot from './MultipleWorkoutsSlot';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

const CalendarDay: React.FC<CalendarDayProps> = ({ day, onWorkoutClick, onMultipleWorkoutsClick, onHourClick, isMobile = false }) => {
  const getShortDayName = (date: Date): string => {
    const dayNames = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];
    return dayNames[date.getDay()];
  };

  const getDayHeaderClass = (): string => {
    let classes = 'p-2 sm:p-3 text-center border-b border-gray-200';
    
    // Проверяем, является ли день выходным (суббота или воскресенье)
    const isWeekend = day.dayOfWeek === 'суббота' || day.dayOfWeek === 'воскресенье';
    
    if (day.isToday) {
      classes += ' bg-blue-50 text-blue-700';
    } else if (isWeekend) {
      classes += ' bg-red-50 text-red-700';
    } else {
      classes += ' bg-gray-50 text-gray-700';
    }
    
    return classes;
  };

  const getHourClass = (hour: any): string => {
    let classes = 'border-b border-gray-100 h-[40px] relative';
    
    if (hour.isCurrentHour) {
      classes += ' bg-orange-50 border-orange-200';
    }
    
    return classes;
  };

  return (
    <div className={`flex flex-col ${isMobile ? 'w-full' : 'flex-1'}`}>
      {/* Заголовок дня */}
      <div className={getDayHeaderClass()}>
        <div className="text-xs sm:text-sm font-medium">
          {getShortDayName(day.date)} {format(day.date, 'd MMM', { locale: ru }).replace('.', '')}
        </div>
      </div>

      {/* Часы */}
      <div className="flex-1">
        {day.hours.map((hour) => (
          <div 
            key={hour.hour} 
            className={`${getHourClass(hour)} ${hour.workouts.length === 0 ? 'cursor-pointer hover:bg-gray-50' : ''}`}
            onClick={hour.workouts.length === 0 && onHourClick ? () => onHourClick(day.date, hour.hour) : undefined}
          >
            {/* Время */}
            <div className="text-xs text-gray-500 mb-0.5 px-1 pt-1">
              {formatTime(hour.hour)}
            </div>
            
            {/* Подсказка для пустых часов */}
            {hour.workouts.length === 0 && (
              <div className="text-[10px] text-gray-400 text-center opacity-0 hover:opacity-100 transition-opacity absolute inset-0 flex items-center justify-center">
                Добавить тренировку
              </div>
            )}
            
            {/* Тренировки - абсолютно позиционированные */}
            <div className="absolute left-0 pointer-events-none" style={{ top: '1px', bottom: '-1px', right: '1px' }}>
              {hour.workouts.length > 2 ? (
                // Если тренировок больше 2, показываем одну плашку с количеством
                <div className="pointer-events-auto h-full">
                  <MultipleWorkoutsSlot
                    workouts={hour.workouts}
                    date={day.date}
                    hour={hour.hour}
                    onClick={onMultipleWorkoutsClick}
                  />
                </div>
              ) : hour.workouts.length === 2 ? (
                // Если тренировок ровно 2, располагаем их горизонтально
                <div className="pointer-events-auto h-full flex">
                  {hour.workouts
                    .filter((workout, index, self) => 
                      index === self.findIndex(w => w.id === workout.id)
                    )
                    .map((workout) => (
                      <div key={workout.id} className="w-1/2 h-full">
                        <WorkoutSlot
                          workout={workout}
                          onClick={onWorkoutClick}
                          isCompact={true}
                        />
                      </div>
                    ))}
                </div>
              ) : (
                // Если тренировка одна, показываем её как обычно
                hour.workouts
                  .filter((workout, index, self) => 
                    index === self.findIndex(w => w.id === workout.id)
                  )
                  .map((workout) => (
                    <div key={workout.id} className="pointer-events-auto h-full w-full">
                      <WorkoutSlot
                        workout={workout}
                        onClick={onWorkoutClick}
                      />
                    </div>
                  ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CalendarDay; 